// src/stores/UserStore.ts
import { makeAutoObservable, runInAction } from "mobx";
import { OrganisationManager } from "../models/OrganisationManager";
import { IOrganisation, IMember, MemberRole } from "../types/organisation";
import { hasRole } from "../utilities/roleUtils";
import {
  hasPlatformOrgAccess,
  isPlatformOrgAdminUserRole,
  isSuperAdminUserRole,
  userHasPlatformOrgAccess,
} from "../utilities/platformAdminAccess";
import { memberHasModeratorPrivileges } from "../utilities/orgAccess";
import { getOrgIdsWithRoleIncludingLegacy } from "../models/user/userOrganisations";
import { roleDashboardRequiresOrganisation } from "../utilities/roleDashboards";
import {
  allowsAnyOrganisationSelection,
  getMemberOrganisationIds,
  resolvePreferredOrganisationId,
} from "../utilities/preferredOrganisation";
import authenticationStore from "./AuthenticationStore";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { isSameStoredUser } from "../utilities/isSameStoredUser";

interface IUser {
  userId: string;
  email?: string;
  displayName?: string;
  userRole?: MemberRole | 'author';
  organisations?: {
    [orgId: string]: IMember;
  };
  projectAdminAccess?: string[];
  /** True when Users/{id}.userProfile.authorKey exists. */
  hasAuthorProfile?: boolean;
  /** SuperAdmins/OrgAdmins claim held even if this session used another login. */
  platformAdminRole?: Extract<MemberRole, 'superAdmin' | 'admin'>;
}

export class UserStore {
  user: IUser | null = null;
  currentOrganisation: IOrganisation | null = null;
  loading = false;
  error: Error | null = null;

  constructor() {
    makeAutoObservable(this);
    this.loadUserFromLocalStorage();
  }

  setError(error: Error | null) {
    this.error = error;
  }

  setUser(user: IUser) {
    if (!user.userRole && (!user.email || !user.displayName)) {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        user = {
          ...user,
          email: user.email || currentUser.email || '',
          displayName: user.displayName || currentUser.displayName || '',
        };
      }
    }

    // Auth hydration rebuilds this object on every Firebase callback. Skip when
    // nothing meaningful changed so observers (workshop / facilitator dashboards)
    // do not remount or refetch.
    if (isSameStoredUser(this.user, user)) {
      return;
    }
    
    this.user = user;
    console.info('[OVH:FacilitatorDash] UserStore.setUser', {
      userId: user.userId,
      userRole: user.userRole,
      orgCount: user.organisations ? Object.keys(user.organisations).length : 0,
    });
    localStorage.setItem('user', JSON.stringify(user));
    if (user.userRole) {
      localStorage.setItem('userRole', user.userRole);
    }
  }

  setCurrentOrganisation(org: IOrganisation | null) {
    console.info('[OVH:FacilitatorDash] UserStore.setCurrentOrganisation', {
      from: this.currentOrganisation?.id,
      to: org?.id,
      name: org?.name,
    });
    this.currentOrganisation = org;
    if (org) {
      localStorage.setItem('currentOrganisation', JSON.stringify(org));
    } else {
      localStorage.removeItem('currentOrganisation');
    }
  }

  clearUser() {
    this.user = null;
    this.currentOrganisation = null;
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('currentOrganisation');
  }

  loadUserFromLocalStorage() {
    const userStr = localStorage.getItem('user');
    const legacyUserRole = localStorage.getItem('userRole');
    const orgStr = localStorage.getItem('currentOrganisation');
    
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (e) {
        console.error('Failed to parse user from localStorage');
        // Fallback to legacy userRole if available
        if (legacyUserRole) {
          this.user = { 
            userId: '',
            userRole: legacyUserRole as MemberRole
          };
        }
      }
    } else if (legacyUserRole) {
      // Support legacy userRole-only storage
      this.user = { 
        userId: '',
        userRole: legacyUserRole as MemberRole
      };
    }

    if (orgStr) {
      try {
        this.currentOrganisation = JSON.parse(orgStr);
      } catch (e) {
        console.error('Failed to parse organisation from localStorage');
      }
    }
  }

  // Legacy getter for backward compatibility
  get userRole(): string {
    return this.user?.userRole || '';
  }

  get hasPlatformAccess(): boolean {
    return userHasPlatformOrgAccess(this.user);
  }

  get isOrgAdmin(): boolean {
    if (!this.user) return false;
    if (this.hasPlatformAccess) return true;
    if (!this.currentOrganisation) return false;
    const member = this.user.organisations?.[this.currentOrganisation.id];
    return member ? hasRole(member.roles, 'organisationAdmin') : false;
  }

  get isOrgModerator(): boolean {
    if (!this.user) return false;
    if (isSuperAdminUserRole(this.user.userRole)) return true;
    if (!this.currentOrganisation) return false;
    const member = this.user.organisations?.[this.currentOrganisation.id];
    // organisationAdmin inherits moderator privileges
    return member ? memberHasModeratorPrivileges(member.roles) : false;
  }

  get currentRole(): MemberRole | null {
    if (!this.user) return null;
    if (isSuperAdminUserRole(this.user.userRole)) return 'superAdmin';
    if (isPlatformOrgAdminUserRole(this.user.userRole)) return 'admin';
    if (!this.currentOrganisation) return null;
    const member = this.user.organisations?.[this.currentOrganisation.id];
    return member ? (member.roles?.[0] || null) : null;
  }

  get currentUser() {
    if (!this.user) return null;

    // Prefer a facilitator membership so dual-role and preview sessions load
    // the facilitator dashboard against the right organisation.
    if (this.user.organisations) {
      const facilitatorOrg = Object.entries(this.user.organisations).find(
        ([_, member]) => hasRole(member.roles, 'facilitator')
      );

      if (facilitatorOrg) {
        const [orgId] = facilitatorOrg;
        return {
          id: this.user.userId,
          email: this.user.email,
          organisationId: orgId,
          role: this.user.userRole === 'facilitator' ? 'facilitator' : this.user.userRole,
        };
      }
    }

    if (hasPlatformOrgAccess(this.user.userRole) || this.hasPlatformAccess) {
      return {
        id: this.user.userId,
        email: this.user.email,
        organisationId: this.currentOrganisation?.id,
        role: this.user.userRole,
      };
    }

    // For other roles, use current organisation
    if (!this.currentOrganisation) return null;
    return {
      id: this.user.userId,
      email: this.user.email,
      organisationId: this.currentOrganisation.id,
      role: this.currentRole
    };
  }

  async loginWithEmailPassword(email: string, password: string, role: MemberRole) {
    try {
      this.loading = true;
      this.error = null;

      // For Project Admin, we don't need to check role in auth
      if (role === 'projectAdmin') {
        const auth = getAuth();
        await signInWithEmailAndPassword(auth, email, password);
        const currentUser = auth.currentUser;
        if (currentUser) {
          this.setUser({
            userId: currentUser.uid,
            email: currentUser.email || '',
            displayName: currentUser.displayName || '',
            userRole: 'projectAdmin',
          });
        }
      } else {
        await authenticationStore.signInWithRole(email, password, role);
      }
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      // Rethrow the error so it propagates to the caller
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Google sign-in for hub role screens.
   * Pass `existingUser` after a mobile redirect resume (auth already completed).
   */
  async loginWithGoogle(role: MemberRole, existingUser?: import('firebase/auth').User) {
    try {
      this.loading = true;
      this.error = null;

      if (existingUser) {
        await authenticationStore.applyAuthenticatedUserRole(existingUser, role);
      } else {
        await authenticationStore.signInWithGoogleRole(role);
      }
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async loginWithAccessCode(accessCode: string, role?: MemberRole) {
    try {
      this.loading = true;
      this.error = null;
      
      const result = await OrganisationManager.verifyAccessCode(accessCode);
      
      if (!result) {
        throw new Error('Invalid access code');
      }

      const { organisationId, role: codeRole } = result;
      
      if (role && role !== codeRole) {
        throw new Error('Access code does not grant the required role');
      }
      
      // Get Organisation details
      const organisation = await OrganisationManager.getOrganisation(organisationId);
      this.setCurrentOrganisation(organisation);
      
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      // Rethrow the error so it propagates to the caller
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async applyPreferredMembershipOrganisation() {
    const memberOrgIds = getMemberOrganisationIds(this.user?.organisations);
    if (memberOrgIds.length === 0) return;

    const allowAny = allowsAnyOrganisationSelection(this.user);
    const currentId = this.currentOrganisation?.id;
    if (allowAny && currentId) return;
    if (currentId && memberOrgIds.includes(currentId)) return;

    await this.switchOrganisation(memberOrgIds[0]);
  }

  async ensureOrganisationForDashboard(path: string) {
    if (!roleDashboardRequiresOrganisation(path)) {
      return;
    }

    const organisations = this.user?.organisations || {};
    const memberOrgIds = getMemberOrganisationIds(organisations);
    const allowAny = allowsAnyOrganisationSelection(this.user);
    if (this.currentOrganisation && (allowAny || memberOrgIds.includes(this.currentOrganisation.id))) {
      return;
    }

    const roleByPath: Record<string, MemberRole> = {
      '/org-dashboard': 'organisationAdmin',
      '/moderator-dashboard': 'moderator',
      '/facilitator-dashboard': 'facilitator',
      '/participant-dashboard': 'member',
    };
    const role = roleByPath[path];
    const roleOrgIds = role
      ? getOrgIdsWithRoleIncludingLegacy(organisations, role)
      : memberOrgIds;
    const orgId = resolvePreferredOrganisationId({
      memberOrgIds: roleOrgIds.length > 0 ? roleOrgIds : memberOrgIds,
      availableOrgIds: memberOrgIds,
      currentOrgId: this.currentOrganisation?.id,
      allowAnyOrganisation: allowAny,
    });
    if (orgId) {
      await this.switchOrganisation(orgId);
    }
  }

  async switchOrganisation(orgId: string) {
    try {
      this.loading = true;
      const org = await OrganisationManager.getOrganisation(orgId);
      this.setCurrentOrganisation(org);
    } catch (error) {
      runInAction(() => {
        this.error = error as Error;
      });
      // Rethrow the error so it propagates to the caller
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  setSelectedProject(projectId: string) {
    if (this.user?.userId) {
      runInAction(() => {
        this.user = {
          ...this.user!,
          projectAdminAccess: [projectId]
        };
        localStorage.setItem('user', JSON.stringify(this.user));
      });
    }
  }
}

export default new UserStore();
